import { FormEvent, useState } from 'react';

import { type ApiOrg, type ApiUpdateOrg, isValidOrgName } from '@kaiizen/kai-pro-api';
import { useCurrentOrg, useUpdateCurrentOrg } from '@kaiizen/kai-pro-api-react-state';

import { CommonActionButton } from '@kaiizen/kai-shiki/react';

import { SuperAdminOrgLiveCheckbox, SuperAdminOrgNameField } from './SuperAdminOrgFields';
import { SuperAdminOrgSwitcher } from './SuperAdminOrgSwitcher';
import { getErrorMessage } from './superAdminToolsLib';
import { DEFAULT_SUPER_ADMIN_TOOLS_PANEL_STYLES, SuperAdminToolsPanelStyles } from './superAdminToolsPanelStyles';
import { SuperAdminToolsRows } from './SuperAdminToolsRows';
import { SuperAdminUserImpersonator } from './SuperAdminUserImpersonator';

/** The edited fields that differ from the org, with the name trimmed as the server stores it. */
const getOrgChanges = (org: ApiOrg, name: string, isLive: boolean): ApiUpdateOrg => ({
  ...(name.trim() !== org.name && { name: name.trim() }),
  ...(isLive !== org.isLive && { isLive }),
});

/**
 * Super admin view of the org they are in: its name, Live flag and id, then a form to rename it, change whether it is
 * live, or both, saved together. Below it, switching to another org or impersonating one of this org's users.
 */
export const SuperAdminCurrentOrg = ({ styles = DEFAULT_SUPER_ADMIN_TOOLS_PANEL_STYLES }: { styles?: SuperAdminToolsPanelStyles }) => {
  const { currentOrg } = useCurrentOrg();
  const updateCurrentOrg = useUpdateCurrentOrg();
  const [edits, setEdits] = useState<ApiUpdateOrg>({});
  const [errorMessage, setErrorMessage] = useState<string>();

  if (!currentOrg) return null;

  const name = edits.name ?? currentOrg.name;
  const isLive = edits.isLive ?? currentOrg.isLive;
  const changes = getOrgChanges(currentOrg, name, isLive);
  const canSave = isValidOrgName(name) && Object.keys(changes).length > 0 && !updateCurrentOrg.isPending;

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setErrorMessage(undefined);
    try {
      await updateCurrentOrg.mutate(changes);
      setEdits({});
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  };

  return (
    <>
      <form css={styles.panel} onSubmit={save}>
        <div css={styles.heading}>{'Current Org'}</div>
        <SuperAdminToolsRows
          rows={[
            { label: 'Name', value: currentOrg.name },
            { label: 'Live', value: currentOrg.isLive ? 'Yes' : 'No' },
            { label: 'Org ID', value: currentOrg.id },
          ]}
          styles={styles}
        />
        <SuperAdminOrgNameField name={name} onChange={newName => setEdits({ ...edits, name: newName })} />
        <SuperAdminOrgLiveCheckbox isLive={isLive} onChange={newIsLive => setEdits({ ...edits, isLive: newIsLive })} styles={styles} />
        {errorMessage && <div css={styles.error}>{errorMessage}</div>}
        <div css={styles.actions}>
          <CommonActionButton type='submit' disabled={!canSave}>{updateCurrentOrg.isPending ? 'Saving...' : 'Save'}</CommonActionButton>
        </div>
      </form>
      <div css={styles.panel}>
        <div css={styles.heading}>{'Switch Org or User'}</div>
        <SuperAdminOrgSwitcher />
        <SuperAdminUserImpersonator />
      </div>
    </>
  );
};
